const MAX_DIMENSION = 1024
const JPEG_QUALITY = 0.85

/**
 * Downscale an image file to at most `maxDimension` px on its longest edge
 * (default 1024, used for avatars; chat sends 1600) and re-encode as JPEG.
 * Phone photos are typically 3-8MB, so uploading the original wastes
 * bandwidth and storage. Returns the original file when it is already small
 * enough or when decoding fails (e.g. HEIC on an unsupported browser) —
 * upload should never be blocked by the resize.
 */
export async function resizeImageFile(
  file: File,
  maxDimension: number = MAX_DIMENSION
): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = maxDimension / Math.max(bitmap.width, bitmap.height)
    if (scale >= 1) {
      bitmap.close()
      return file
    }

    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return file
    }
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    )
    if (!blob) return file

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo'
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
  } catch {
    return file
  }
}

const THUMBNAIL_JPEG_QUALITY = 0.8

// Must match THUMB_VARIANTS in apps/api/src/services/thumbnail.service.ts.
//  - thumb: 400px square, center-cropped — grid tiles (~115 CSS px) and avatars.
//  - large: fits inside 1200px, no crop — the aircraft hero image.
export const THUMBNAIL_VARIANTS = {
  thumb: { size: 400, fit: 'cover' },
  large: { size: 1200, fit: 'inside' },
} as const

export type ThumbnailVariant = keyof typeof THUMBNAIL_VARIANTS

/**
 * Produce a resized JPEG for the given variant. EXIF orientation is applied by
 * `imageOrientation: 'from-image'`. Returns null when the browser cannot
 * decode the file (e.g. HEIC on Android Chrome) or the image is already
 * smaller than a 'inside' target — the caller then simply skips the variant
 * and the UI falls back to the original.
 */
export async function makeThumbnailFile(
  file: File,
  variant: ThumbnailVariant = 'thumb'
): Promise<File | null> {
  const { size, fit } = THUMBNAIL_VARIANTS[variant]
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })

    let sx = 0
    let sy = 0
    let sw = bitmap.width
    let sh = bitmap.height
    let width: number = size
    let height: number = size
    if (fit === 'cover') {
      const side = Math.min(bitmap.width, bitmap.height)
      sx = Math.round((bitmap.width - side) / 2)
      sy = Math.round((bitmap.height - side) / 2)
      sw = side
      sh = side
    } else {
      const scale = size / Math.max(bitmap.width, bitmap.height)
      if (scale >= 1) {
        // Original already fits; serving it as-is costs nothing extra.
        bitmap.close()
        return null
      }
      width = Math.round(bitmap.width * scale)
      height = Math.round(bitmap.height * scale)
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return null
    }
    ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, width, height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', THUMBNAIL_JPEG_QUALITY)
    )
    if (!blob) return null
    return new File([blob], `${variant}.jpg`, { type: 'image/jpeg' })
  } catch {
    return null
  }
}
